import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  ArrowRight, 
  ShieldCheck, 
  Zap, 
  Users, 
  Repeat, 
  MessageSquare, 
  Heart,
  TrendingUp,
  Search,
  Package,
  ArrowRightLeft,
  Car,
  Book,
  Palette,
  Armchair,
  Bitcoin,
  Smartphone,
  Watch,
  Camera,
  Layers
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Section = ({ children, className = "" }) => (
  <section className={`py-16 md:py-24 px-4 ${className}`}>
    <div className="max-w-5xl mx-auto">
      {children}
    </div>
  </section>
);

const FeatureCard = ({ icon: Icon, title, description, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    className="p-8 rounded-[2.5rem] bg-card/40 backdrop-blur-md border border-border/50 shadow-xl hover:shadow-2xl transition-all group relative overflow-hidden"
  >
    <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-bl-[5rem] -mr-8 -mt-8 group-hover:bg-primary/10 transition-colors" />
    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
      <Icon className="text-primary" size={28} />
    </div>
    <h3 className="text-xl font-black mb-3">{title}</h3>
    <p className="text-muted-foreground leading-relaxed">{description}</p>
  </motion.div>
);

export default function LandingStory({ isPage = false }) {
  const { t, language } = useLanguage();

  const containerClasses = isPage 
    ? "relative" 
    : "relative overflow-y-auto max-h-[70vh] custom-scrollbar px-2";

  return (
    <div className={containerClasses}>
      {/* 1. Hero Concept */}
      <Section className="text-center pt-8 md:pt-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-xs font-black uppercase tracking-widest border border-primary/20">
            <Layers size={14} />
            {t('landing.welcome', 'The Future of Trading')}
          </div>
          <h2 className="text-4xl md:text-7xl font-black leading-tight tracking-tighter">
            {language === 'he' ? (
                <>למה לקנות כשאפשר <span className="text-primary italic">להחליף?</span></>
            ) : (
                <>Why Buy When You Can <span className="text-primary italic">Swap?</span></>
            )}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed font-medium">
            {t('landing.conceptDesc')}
          </p>
        </motion.div>
      </Section>

      {/* 2. Swapping Ecosystem Visual */}
      <Section className="bg-muted/30 rounded-[4rem] border border-border/50">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
              {t('howItWorks.title', 'The Swapping Ecosystem')}
            </h2>
            <div className="space-y-8">
              {[
                { 
                  icon: Car, 
                  title: language === 'he' ? 'מוצרים פיזיים' : 'Physical Goods',
                  desc: language === 'he' ? 'ממכוניות ורהיטים ועד מכשירי חשמל וספרים.' : 'From cars and furniture to electronics and books.'
                },
                { 
                  icon: Bitcoin, 
                  title: language === 'he' ? 'נכסים דיגיטליים' : 'Digital Assets',
                  desc: language === 'he' ? 'החלפת מטבעות קריפטו, פריטי אספנות דיגיטליים ועוד.' : 'Swap crypto, digital collectibles, and more.'
                },
                { 
                  icon: Repeat, 
                  title: language === 'he' ? 'איזון כספי' : 'Cash Balancing',
                  desc: language === 'he' ? 'ניתן להוסיף כסף כדי להשלים פערים בערך העסקה.' : 'Add cash to bridge any value gaps in your trades.'
                }
              ].map((step, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2 }}
                  className="flex gap-6 items-start group"
                >
                  <div className="w-14 h-14 rounded-2xl bg-card border border-border shadow-md flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-primary-content transition-all duration-300">
                    <step.icon size={28} />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-xl font-black">{step.title}</h4>
                    <p className="text-muted-foreground font-medium">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="relative hidden md:block">
             {/* Dynamic Background Glow */}
             <div className="absolute inset-0 bg-primary/5 blur-[80px] rounded-full animate-pulse" />
             
             <div className="aspect-square rounded-[3rem] bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 flex items-center justify-center p-8 relative">
                {/* Orbiting Icons */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                   {[
                     { Icon: Car, angle: 0, color: "text-blue-500" },
                     { Icon: Book, angle: 60, color: "text-emerald-500" },
                     { Icon: Palette, angle: 120, color: "text-rose-500" },
                     { Icon: Armchair, angle: 180, color: "text-amber-500" },
                     { Icon: Bitcoin, angle: 240, color: "text-yellow-500" },
                     { Icon: Smartphone, angle: 300, color: "text-indigo-500" }
                   ].map((item, i) => (
                     <div 
                        key={i}
                        className="absolute"
                        style={{ transform: `rotate(${item.angle}deg) translateY(-140px)` }}
                     >
                        <motion.div
                          whileHover={{ scale: 1.2 }}
                          animate={{ rotate: -360 }}
                          transition={{ 
                            rotate: { duration: 40, repeat: Infinity, ease: "linear" },
                            scale: { type: "spring", stiffness: 300, damping: 15 }
                          }}
                          className={`w-16 h-16 bg-card/80 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 flex items-center justify-center text-3xl ${item.color} cursor-pointer group hover:border-primary/50 transition-colors`}
                        >
                           <item.Icon size={32} strokeWidth={1.5} className="group-hover:drop-shadow-[0_0_8px_rgba(var(--primary),0.4)] transition-all" />
                        </motion.div>
                     </div>
                   ))}
                </motion.div>

                {/* Central Hub */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.05, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="w-48 h-48 rounded-full bg-primary/10 backdrop-blur-2xl border-4 border-dashed border-primary/30 flex items-center justify-center relative shadow-[0_0_40px_rgba(var(--primary),0.15)] group"
                >
                   <div className="absolute inset-0 rounded-full bg-primary/5 animate-ping opacity-20" />
                   <ArrowRightLeft size={80} className="text-primary drop-shadow-[0_0_15px_rgba(var(--primary),0.3)] group-hover:scale-110 transition-transform duration-500" />
                   
                   {/* Orbit Ring */}
                   <div className="absolute inset-[-15px] border border-primary/10 rounded-full" />
                </motion.div>
             </div>
          </div>
        </div>
      </Section>

      {/* 3. Features Grid */}
      <Section>
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight">{t('landing.whyUs', 'Built for the Community')}</h2>
          <p className="text-muted-foreground text-lg font-medium max-w-2xl mx-auto">{t('landing.whyUsDesc')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard 
            icon={ShieldCheck} 
            title={t('secure', 'Secure Trading')} 
            description={t('landing.secureDesc')}
            delay={0.1}
          />
          <FeatureCard 
            icon={TrendingUp} 
            title={t('landing.valueTracking', 'Value Matching')} 
            description={t('landing.valueDesc')}
            delay={0.2}
          />
          <FeatureCard 
            icon={Heart} 
            title={t('community', 'Community Driven')} 
            description={language === 'he' ? 'מערכת דירוגים וביקורות שבונה אמון בין המשתמשים.' : 'A rating and review system that builds trust within the community.'}
            delay={0.3}
          />
        </div>
      </Section>

      {/* 4. Social Impact Banner */}
      <Section className="bg-primary text-primary-content overflow-hidden relative rounded-[3rem] shadow-2xl mx-4 my-12">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[400px] h-[400px] bg-white/10 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-[300px] h-[300px] bg-black/10 rounded-full blur-3xl opacity-50"></div>
        
        <div className="grid md:grid-cols-2 gap-12 items-center relative z-10 p-8 md:p-16 text-start">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-6xl font-black leading-tight tracking-tighter">
               {language === 'he' ? 'יחד אנחנו יוצרים כלכלה חכמה יותר' : 'Together We Create a Smarter Economy'}
            </h2>
            <p className="text-primary-content/80 text-xl font-medium leading-relaxed">
              {t('landing.socialDesc')}
            </p>
            <div className="flex flex-wrap gap-12 pt-4">
              <div className="space-y-1">
                <p className="text-5xl font-black tracking-tighter">10K+</p>
                <p className="text-sm opacity-70 font-black uppercase tracking-widest">{t('items', 'Items')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-5xl font-black tracking-tighter">5K+</p>
                <p className="text-sm opacity-70 font-black uppercase tracking-widest">{t('users', 'Users')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-5xl font-black tracking-tighter">2K+</p>
                <p className="text-sm opacity-70 font-black uppercase tracking-widest">{t('trades', 'Trades')}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6 rotate-3">
             {[
               { icon: Car, color: "bg-white/10" },
               { icon: Smartphone, color: "bg-white/5" },
               { icon: Palette, color: "bg-white/5" },
               { icon: Bitcoin, color: "bg-white/10" }
             ].map((item, i) => (
               <motion.div 
                 key={i} 
                 whileHover={{ scale: 1.05, rotate: -3 }}
                 className={`aspect-square ${item.color} backdrop-blur-md rounded-3xl border border-white/10 flex items-center justify-center text-white/40 shadow-xl`}
               >
                  <item.icon size={64} strokeWidth={1} />
               </motion.div>
             ))}
          </div>
        </div>
      </Section>

      {/* 5. Final CTA */}
      {isPage && (
        <Section className="text-center pb-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="space-y-12"
          >
            <div className="space-y-4">
              <h2 className="text-4xl md:text-7xl font-black tracking-tighter uppercase italic">
                {language === 'he' ? 'מוכנים להחליף?' : 'Ready to Swap?'}
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium">
                {t('landing.readyDesc')}
              </p>
            </div>
            
            <Link 
              to="/browse"
              className="group relative inline-flex items-center gap-4 px-12 py-6 rounded-full bg-primary text-primary-content font-black text-2xl shadow-[0_20px_50px_rgba(var(--primary),0.3)] hover:scale-105 active:scale-95 transition-all overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-3">
                {t('explore', 'Start Exploring')}
                <ArrowRight className="group-hover:translate-x-2 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </Link>
          </motion.div>
        </Section>
      )}
    </div>
  );
}
